import { formatDate } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { EmailRecord } from "@models/email-templates/email-record";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterOption } from "@models/grid/grid-filter";
import {
  ExportEntityListModalComponent
} from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import { excelTitleForReport } from "@shared/utils/excel-title-for-report";
import {
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  ExcelRow,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams
} from "ag-grid-community";
import { take } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class OutgoingEmailsExportService {
  private modalFormsService = inject(ModalFormsService);

  exportEmailMessagesList(
    gridApi: GridApi,
    colDefs: ColDef[],
    checkedEmailRecords: EmailRecord[],
    totalRows: number,
    isBulkExport: boolean,
    exportType: ExportTypeEnum
  ): void {
    const modalRef = this.modalFormsService.openModal(ExportEntityListModalComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ExportEntityListModalComponent;

    componentInstance.title = 'Export Outgoing Email Messages';
    componentInstance.subTitle = 'email messages selected';
    componentInstance.colDefs = colDefs;
    componentInstance.numberOfCompanies = isBulkExport ? checkedEmailRecords.length : totalRows;
    componentInstance.exportType = exportType;
    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          gridApi.exportDataAsExcel(params);
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport ? {
            columnKeys: columnForExport,
            onlySelected: true
          } : { columnKeys: columnForExport };
          gridApi.exportDataAsCsv(params);
        }
      });
  }

  exportParams() {
    return {
      processCellCallback(params: ProcessCellForExportParams): string {
        const value: unknown = params.value;
        const emailMessageData = params.node?.data as EmailRecord;

        if (params.column.getColId() === 'createdOn') {
          return formatDate(emailMessageData.createdOn ?? '', 'dd MMM yyyy HH:mm', 'en-US');
        } else if (params.column.getColId() === 'type' || params.column.getColId() === 'status' || params.column.getColId() === 'resolvedStatus') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
          const options: FilterOption[] = params.column.getColDef()?.filterParams?.filterOptions ?? [];
          return options.find(o => o.value == value)?.label ?? '';
        }

        return value as string;
      },
      processHeaderCallback(params: ProcessHeaderForExportParams) {
        const colDef = params.column.getColDef() as ColumnWithExportName;
        return colDef?.exportColumnName ?? colDef.headerName;
      },
      prependContent: this.getExcelTitleRows(3),
      sheetName: 'Outgoing Email Messages',
      fileName: 'Outgoing Email Messages ' + formatDate(new Date(), 'dd-MM-yyyy', 'en-US')
    };
  }

  getExcelTitleRows(numberColumnForExport: number): ExcelRow[] {
    return excelTitleForReport(numberColumnForExport, 'Outgoing Email Messages');
  }
}
